import { domReady, dispatchEvent } from '../modules/utils.js';

class FullMenu extends HTMLElement {
  static get observedAttributes() {
    return [];
  }

  attributeChangedCallback(name, oldValue, newValue) { }

  async connectedCallback() {
    await domReady();

    this.refs = {
      panel: this.querySelector('.fullmenu-panel'),
      navViewport: this.querySelector('.fullmenu-nav-viewport'),
      navSubs: [...this.querySelectorAll('.fullmenu-nav-sub')],
    };

    this.#setListeners();
  }

  disconnectedCallback() {
    this.#unsetListeners();
  }

  #setListeners() {
    this.refs.panel?.addEventListener('click', this.#onPanelClick);
    this.refs.navViewport?.addEventListener('click', this.#onNavClick);
    document.addEventListener('mts:fullmenu-toggle', this.toggle);
    document.addEventListener('keydown', this.#onKeydown);
  }

  #unsetListeners() {
    this.refs.panel?.removeEventListener('click', this.#onPanelClick);
    this.refs.navViewport?.removeEventListener('click', this.#onNavClick);
    document.removeEventListener('mts:fullmenu-toggle', this.toggle);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  #onPanelClick = (event) => {
    if (event.target === this.refs.panel) {
      this.close();
    }
  };

  #onNavClick = (event) => {
    const back = event.target.closest('.fullmenu-nav-back');
    if (back) {
      this.#closeNavSub();
      return;
    }

    const parentLink = event.target.closest('.fullmenu-nav-list a[data-nav-id]');
    if (parentLink) {
      event.preventDefault();
      this.#openNavSub(parentLink.dataset.navId);
    }
  };

  #openNavSub(navId) {
    for (const sub of this.refs.navSubs) {
      sub.toggleAttribute('data-open', sub.dataset.navSub === navId);
    }
    this.refs.navViewport?.setAttribute('data-open-sub', '');
  }

  #closeNavSub() {
    for (const sub of this.refs.navSubs) {
      sub.removeAttribute('data-open');
    }
    this.refs.navViewport?.removeAttribute('data-open-sub');
  }

  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.hasAttribute('data-open')) {
      this.close();
    }
  };

  #setSiblingsInert(isInert) {
    for (const el of document.body.children) {
      if (el !== this && el.tagName !== 'TOP-BAR') {
        el.toggleAttribute('inert', isInert);
      }
    }
  }

  open = () => {
    if (this.hasAttribute('data-open')) return;

    this.lastFocused = document.activeElement;
    this.removeAttribute('inert');
    this.setAttribute('aria-hidden', 'false');
    this.#setSiblingsInert(true);
    document.documentElement.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      this.setAttribute('data-open', '');
    });

    this.refs.panel?.focus();
    dispatchEvent('mts:fullmenu-open');
  };

  close = () => {
    if (!this.hasAttribute('data-open')) return;

    this.#closeNavSub();
    this.removeAttribute('data-open');
    this.#setSiblingsInert(false);
    document.documentElement.style.overflow = '';
    dispatchEvent('mts:fullmenu-close');

    const onTransitionEnd = (event) => {
      if (event.target !== this.refs.panel) return;
      this.refs.panel.removeEventListener('transitionend', onTransitionEnd);
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('inert', '');
    };
    this.refs.panel?.addEventListener('transitionend', onTransitionEnd);

    if (this.lastFocused instanceof HTMLElement) {
      this.lastFocused.focus();
    }
  };

  toggle = () => {
    if (this.hasAttribute('data-open')) {
      this.close();
    } else {
      this.open();
    }
  };
}

customElements.define('full-menu', FullMenu);
